import { Shot, ShotVersion, SubtitleEraseTask } from '../../../types';
import { shotNavStatusColors } from '../constants';
import { buildHighlightStyle } from '../shotHighlight';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ShotNavigationDockProps {
  episodeIndex: number;
  shots: Shot[];
  shotVersions: Record<string, ShotVersion[]>;
  activeShotId: string | null;
  shotWeights: Record<string, number>;
  playedVideos: Set<string>;
  subtitleEraseStatus: Record<string, SubtitleEraseTask>;
  prevEpisodeId: string | null;
  nextEpisodeId: string | null;
  t: Translate;
  onNavigateEpisode: (episodeId: string) => void;
}

export default function ShotNavigationDock({
  episodeIndex,
  shots,
  shotVersions,
  activeShotId,
  shotWeights,
  playedVideos,
  subtitleEraseStatus,
  prevEpisodeId,
  nextEpisodeId,
  t,
  onNavigateEpisode,
}: ShotNavigationDockProps) {
  return (
    <>
      <style>{`
        .shot-nav-dock {
          position: sticky;
          top: calc(var(--header-height) + 12px);
          align-self: flex-start;
          min-width: 93px;
          width: max-content;
          max-width: 150px;
          flex-shrink: 0;
          max-height: calc(100vh - var(--header-height) - 32px);
          display: flex;
          flex-direction: column;
          background: var(--surface-2);
          border-radius: var(--radius-sm);
          margin-top: 20px;
        }
        .shot-nav-footer-btn {
          font-size: 0.81rem;
          padding: 0.25rem 0.1rem;
          line-height: 1.1;
          white-space: nowrap;
        }
        @media (prefers-reduced-motion: reduce) {
          .shot-nav-dock button { transition: none !important; }
        }
      `}</style>
      <aside className="shot-nav-dock" aria-label={t('shotList.navLabel', { n: episodeIndex })}>
        <div style={{ padding: '0.4rem 0.4rem', fontSize: '0.91rem', fontWeight: 700, textAlign: 'left', color: 'var(--text)' }}>
          {t('episodeInfo.episodeN', { n: episodeIndex })}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.2rem 0', display: 'flex', flexDirection: 'column', gap: '1px' }}>
          {shots.map(shot => {
            const versions = shotVersions[shot.id] || [];
            const hasReadyVideo = versions.some(version => version.taskStatus === 'completed' && version.videoURL);
            const isGenerating = shot.status === 'generating'
              || versions.some(version => version.taskStatus === 'generating' || version.taskStatus === 'pending');
            const shotStatus: 'green' | 'yellow' | 'red' =
              hasReadyVideo ? 'green'
              : isGenerating ? 'yellow'
              : 'red';
            const colorSet = shotNavStatusColors[shotStatus];
            const isActive = activeShotId === shot.id; // 仅用于 aria-current
            const weight = shotWeights[shot.id] ?? 0;
            const highlight = buildHighlightStyle(weight, colorSet);
            const shotHasNew = versions.some(version => (version.taskStatus === 'completed' && version.videoURL && !playedVideos.has(version.id))
              || (subtitleEraseStatus[version.id]?.status === 'completed' && subtitleEraseStatus[version.id]?.resultVideoUrl && !playedVideos.has(`erased:${version.id}`)));
            return (
              <button
                key={shot.id}
                id={`nav-shot-${shot.id}`}
                type="button"
                aria-current={isActive ? 'location' : undefined}
                onClick={() => {
                  const element = document.getElementById('shot-' + shot.id);
                  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                title={t('shotList.navTitle', { index: shot.index, status: shotStatus === 'green' ? t('shotList.navStatusGreen') : shotStatus === 'yellow' ? t('shotList.navStatusYellow') : t('shotList.navStatusRed') })}
                style={{
                  background: highlight.background,
                  border: 'none',
                  cursor: 'pointer', flexShrink: 0,
                  display: 'flex', alignItems: 'center', gap: '3px',
                  maxWidth: '100%', overflow: 'hidden',
                  textAlign: 'left',
                  padding: '0.28rem 0.4rem', borderRadius: '4px',
                  fontSize: '1.04rem',
                  color: highlight.color,
                  fontWeight: highlight.fontWeight, lineHeight: 1.3,
                  fontFamily: 'inherit',
                  transition: 'background-color 0.12s ease-out, color 0.12s ease-out, box-shadow 0.12s ease-out',
                  boxShadow: highlight.boxShadow,
                }}
                onMouseEnter={event => { if (weight <= 0) event.currentTarget.style.background = colorSet.hoverBg; }}
                onMouseLeave={event => { event.currentTarget.style.background = highlight.background; }}
              >
                #{shot.index}
                {(shot.directorLabels || []).map((label, labelIndex) => (
                  <span
                    key={`${labelIndex}:${label}`}
                    data-director-label
                    title={label}
                    style={{
                      minWidth: 0, maxWidth: '72px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      fontSize: '0.64rem', fontWeight: 600, padding: '1px 4px', borderRadius: '999px',
                      background: 'color-mix(in srgb, var(--theme-storyboard) 18%, var(--surface))',
                      color: 'var(--theme-storyboard)', flexShrink: 1,
                    }}
                  >
                    {label}
                  </span>
                ))}
                {(shot.pendingCommentCount ?? 0) > 0 && (
                  <span style={{ width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%', flexShrink: 0 }} />
                )}
                {shotHasNew && <span style={{ background: '#22c55e', color: '#fff', fontSize: '0.64rem', fontWeight: 700, padding: '1px 2px', borderRadius: '2px', lineHeight: 1 }}>NEW</span>}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '4px' }}>
          <button
            className="btn btn-ghost shot-nav-footer-btn"
            disabled={!prevEpisodeId}
            onClick={() => prevEpisodeId && onNavigateEpisode(prevEpisodeId)}
            title={t('nav.prevEpisodeShort')}
          >{t('nav.prevEpisodeShort')}</button>
          <button
            className="btn btn-ghost shot-nav-footer-btn"
            disabled={!nextEpisodeId}
            onClick={() => nextEpisodeId && onNavigateEpisode(nextEpisodeId)}
            title={t('nav.nextEpisodeShort')}
          >{t('nav.nextEpisodeShort')}</button>
          <button
            className="btn btn-ghost shot-nav-footer-btn"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            title={t('nav.toTopTitle')}
          >{t('nav.toTop')}</button>
          <button
            className="btn btn-ghost shot-nav-footer-btn"
            onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })}
            title={t('nav.toBottomTitle')}
          >{t('nav.toBottom')}</button>
        </div>
      </aside>
    </>
  );
}
